import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lightbulb, Calendar, MapPin, Clock, DollarSign, Settings, RefreshCw, Heart, Share } from 'lucide-react';

const AIDateIdeaGenerator = () => {
  const [dateIdeas, setDateIdeas] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preferences, setPreferences] = useState({
    budget: '',
    activity_type: '',
    time_preference: ''
  });
  const [showPreferences, setShowPreferences] = useState(false);
  const [error, setError] = useState('');

  const budgetOptions = [
    { value: 'free', label: 'Free', icon: '🆓' },
    { value: 'low', label: 'Low ($0-$25)', icon: '💰' },
    { value: 'medium', label: 'Medium ($25-$75)', icon: '💵' },
    { value: 'high', label: 'High ($75+)', icon: '💸' }
  ];

  const activityOptions = [
    { value: 'active', label: 'Active & Outdoorsy', icon: '🏃‍♂️' },
    { value: 'cultural', label: 'Cultural & Arts', icon: '🎭' },
    { value: 'food', label: 'Food & Dining', icon: '🍽️' },
    { value: 'entertainment', label: 'Entertainment', icon: '🎬' },
    { value: 'adventure', label: 'Adventure & Unique', icon: '🚁' },
    { value: 'relaxed', label: 'Relaxed & Cozy', icon: '☕' }
  ];

  const timeOptions = [
    { value: 'morning', label: 'Morning', icon: '🌅' },
    { value: 'afternoon', label: 'Afternoon', icon: '☀️' },
    { value: 'evening', label: 'Evening', icon: '🌆' },
    { value: 'weekend', label: 'Weekend', icon: '📅' }
  ];

  const generateDateIdeas = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai-date-ideas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          preferences: preferences.budget || preferences.activity_type || preferences.time_preference 
            ? preferences 
            : null
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate date ideas');
      }

      const data = await response.json();
      setDateIdeas(data.date_ideas);
    } catch (error) {
      console.error('Error generating date ideas:', error);
      setError('Unable to generate date ideas. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      // Could add a toast notification here
    });
  };

  const shareIdea = (idea) => {
    if (navigator.share) {
      navigator.share({
        title: idea.title,
        text: idea.description,
      });
    } else {
      copyToClipboard(`${idea.title}: ${idea.description}`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-purple-50 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="flex justify-center items-center mb-4">
            <Lightbulb className="h-8 w-8 text-rose-500 mr-3" />
            <h1 className="text-4xl font-bold bg-gradient-to-r from-rose-600 to-purple-600 bg-clip-text text-transparent">
              AI Date Idea Generator
            </h1>
          </div>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Get personalized, creative date ideas powered by AI. Tailored to your interests and preferences for memorable experiences.
          </p>
        </motion.div>

        {/* Controls */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl shadow-lg p-6 mb-8"
        >
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <button
              onClick={() => setShowPreferences(!showPreferences)}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-rose-600 transition-colors"
            >
              <Settings className="h-4 w-4" />
              {showPreferences ? 'Hide' : 'Show'} Preferences
            </button>
            
            <div className="flex gap-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={generateDateIdeas}
                disabled={loading}
                className="bg-gradient-to-r from-rose-500 to-purple-600 text-white px-8 py-3 rounded-xl font-semibold flex items-center gap-2 shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
              >
                {loading ? (
                  <RefreshCw className="h-5 w-5 animate-spin" />
                ) : (
                  <Lightbulb className="h-5 w-5" />
                )}
                {loading ? 'Generating...' : 'Generate Ideas'}
              </motion.button>
            </div>
          </div>

          {/* Preferences Panel */}
          <AnimatePresence>
            {showPreferences && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="mt-6 pt-6 border-t border-gray-100"
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Budget Preference */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                      <DollarSign className="h-4 w-4" />
                      Budget Preference
                    </label>
                    <div className="space-y-2">
                      {budgetOptions.map((option) => (
                        <label key={option.value} className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="radio"
                            name="budget"
                            value={option.value}
                            checked={preferences.budget === option.value}
                            onChange={(e) => setPreferences({...preferences, budget: e.target.value})}
                            className="text-rose-600 focus:ring-rose-500"
                          />
                          <span className="text-lg">{option.icon}</span>
                          <span className="text-sm">{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Activity Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Activity Type
                    </label>
                    <div className="space-y-2">
                      {activityOptions.map((option) => (
                        <label key={option.value} className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="radio"
                            name="activity_type"
                            value={option.value}
                            checked={preferences.activity_type === option.value}
                            onChange={(e) => setPreferences({...preferences, activity_type: e.target.value})}
                            className="text-rose-600 focus:ring-rose-500"
                          />
                          <span className="text-lg">{option.icon}</span>
                          <span className="text-sm">{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Time Preference */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Time Preference
                    </label>
                    <div className="space-y-2">
                      {timeOptions.map((option) => (
                        <label key={option.value} className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="radio"
                            name="time_preference"
                            value={option.value}
                            checked={preferences.time_preference === option.value}
                            onChange={(e) => setPreferences({...preferences, time_preference: e.target.value})}
                            className="text-rose-600 focus:ring-rose-500"
                          />
                          <span className="text-lg">{option.icon}</span>
                          <span className="text-sm">{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Error Display */}
        {error && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 text-red-800"
          >
            {error}
          </motion.div>
        )}

        {/* Date Ideas Display */}
        <AnimatePresence>
          {dateIdeas && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="space-y-8"
            >
              {/* Header with AI Generated Badge */}
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-800">
                  Your Personalized Date Ideas
                </h2>
                <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  dateIdeas.ai_generated 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {dateIdeas.ai_generated ? '🤖 AI Generated' : '📝 Default Ideas'}
                </div>
              </div>

              {/* Creative Ideas Section */}
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Heart className="h-5 w-5 text-rose-500" />
                  Creative Date Ideas
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {dateIdeas.creative_ideas?.map((idea, index) => (
                    <DateIdeaCard 
                      key={index} 
                      idea={idea} 
                      index={index}
                      onShare={() => shareIdea(idea)}
                    />
                  ))}
                </div>
              </div>

              {/* Location-Based Ideas Section */}
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-purple-500" />
                  Location-Based Ideas
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {dateIdeas.location_based?.map((idea, index) => (
                    <DateIdeaCard 
                      key={index} 
                      idea={idea} 
                      index={index + (dateIdeas.creative_ideas?.length || 0)}
                      onShare={() => shareIdea(idea)}
                    />
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

// Date Idea Card Component
const DateIdeaCard = ({ idea, index, onShare }) => {
  const getCostColor = (costLevel) => {
    const level = costLevel?.toLowerCase();
    if (level === 'free' || level === 'free-low') return 'text-green-600 bg-green-50';
    if (level === 'low') return 'text-blue-600 bg-blue-50';
    if (level === 'medium') return 'text-orange-600 bg-orange-50';
    if (level === 'high') return 'text-red-600 bg-red-50';
    return 'text-gray-600 bg-gray-50';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow border border-gray-100"
    >
      <div className="space-y-4">
        <div className="flex items-start justify-between">
          <h4 className="text-lg font-bold text-gray-800 flex-1 pr-2">
            {idea.title}
          </h4>
          <button
            onClick={onShare}
            className="p-2 text-gray-400 hover:text-rose-500 transition-colors"
          >
            <Share className="h-4 w-4" />
          </button>
        </div>
        
        <p className="text-gray-600 text-sm leading-relaxed">
          {idea.description}
        </p>
        
        <div className="bg-rose-50 p-3 rounded-lg">
          <p className="text-rose-800 text-sm font-medium">
            💝 {idea.why_special}
          </p>
        </div>
        
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div className={`px-3 py-2 rounded-lg font-medium ${getCostColor(idea.cost_level)}`}>
            💰 {idea.cost_level}
          </div>
          <div className="px-3 py-2 rounded-lg bg-purple-50 text-purple-600 font-medium">
            ⏱️ {idea.duration}
          </div>
        </div>
        
        <div className="space-y-2 text-xs text-gray-600">
          <div>
            <span className="font-medium">Best Time:</span> {idea.best_time}
          </div>
          <div>
            <span className="font-medium">Prep Needed:</span> {idea.prep_needed}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default AIDateIdeaGenerator;